/**
 * Standalone portal token refresh module.
 *
 * Usable from route loaders, axios interceptors, and hooks alike
 * (not a React hook -- pure async function).
 *
 * Includes a mutex so concurrent 401s do not fire multiple refresh
 * requests at once.
 */

import { API_CONFIG } from '@/config/api';
import {
  getPortalAuth,
  setPortalAuth,
  clearPortalAuth,
} from './storage';

const API_URL = API_CONFIG.URL;

export interface RefreshResult {
  success: boolean;
  token?: string;
}

// Simple mutex: while a refresh is in-flight, all callers await the same promise.
let inflightRefresh: Map<string, Promise<RefreshResult>> = new Map();

/**
 * Attempt to refresh the portal access token for a given client.
 *
 * - Reads the refresh token from localStorage.
 * - POSTs to /portal/auth/refresh.
 * - On success, stores the new access + refresh tokens and returns the new access token.
 * - On failure (expired, revoked, replay), clears auth and returns { success: false }.
 */
export async function attemptTokenRefresh(clientId: string): Promise<RefreshResult> {
  // Deduplicate concurrent calls for the same client
  const existing = inflightRefresh.get(clientId);
  if (existing) {
    return existing;
  }

  const promise = doRefresh(clientId);
  inflightRefresh.set(clientId, promise);

  try {
    return await promise;
  } finally {
    inflightRefresh.delete(clientId);
  }
}

async function doRefresh(clientId: string): Promise<RefreshResult> {
  const auth = getPortalAuth(clientId);
  if (!auth?.refreshToken) {
    return { success: false };
  }

  try {
    const response = await fetch(`${API_URL}/portal/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: auth.refreshToken }),
    });

    if (!response.ok) {
      // Any non-2xx means the refresh token is dead
      clearPortalAuth(clientId);
      return { success: false };
    }

    const data = await response.json();

    if (!data.success || !data.accessToken || !data.refreshToken) {
      clearPortalAuth(clientId);
      return { success: false };
    }

    setPortalAuth(clientId, {
      token: data.accessToken,
      refreshToken: data.refreshToken,
      role: auth.role,
      clientName: auth.clientName,
    });

    return { success: true, token: data.accessToken };
  } catch {
    // Network error -- don't clear auth, the user might just be offline
    return { success: false };
  }
}
